import React, { useEffect, useState } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { useNavigate } from "react-router-dom";

const SettingsForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    email: "",
    mobile: "",
    whatsappnumber: "",
    upi_id: "",
    merchant_id: "",
    min_batting_rate: "",
    max_batting_rate: "",
    min_withdrawal_rate: "",
    max_withdrawal_rate: "",
    min_deposite_rate: "",
    max_deposite_rate: "",
    min_transfer: "",
    max_transfer: "",
    min_bid_amount: "",
    max_bid_amount: "",
    welcome_bonus: "",
    min_withdrawals_per_day: "",

    // ✅ NEW FIELDS
    min_single_ank: "",
    max_single_ank: "",
    min_jodi_ank: "",
    max_jodi_ank: "",
    min_pana_ank: "",
    max_pana_ank: "",

    openTime: "",
    closeTime: "",
    closeWeek: "Sunday",
    whatsapp_deposit_option: "active",
    withdraw_option: "active",
    global_betting: false,
  });


  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);

  // Fetch settings from API on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(`/api/settings/general`);
        const data = response.data[0];

        // Custom function to handle null/undefined but keep 0
        const getValue = (value) => {
          return value === null || value === undefined ? "" : value;
        };

        setFormData({
          id: data._id,
          name: data.name || "",
          email: data.email || "",
          mobile: data.mobile || "",
          whatsappnumber: data.whatsappnumber || "",
          upi_id: data.upi_id || "",
          merchant_id: data.merchant_id || "",
          min_batting_rate: getValue(data.min_batting_rate),
          max_batting_rate: getValue(data.max_batting_rate),
          min_withdrawal_rate: getValue(data.min_withdrawal_rate),
          max_withdrawal_rate: getValue(data.max_withdrawal_rate),
          min_deposite_rate: getValue(data.min_deposite_rate),
          max_deposite_rate: getValue(data.max_deposite_rate),
          min_transfer: getValue(data.min_transfer),
          max_transfer: getValue(data.max_transfer),
          min_bid_amount: getValue(data.min_bid_amount),
          max_bid_amount: getValue(data.max_bid_amount),
          welcome_bonus: getValue(data.welcome_bonus),
          min_withdrawals_per_day: getValue(data.min_withdrawals_per_day),

          // ✅ NEW FIELDS - use getValue instead of ||
          min_single_ank: getValue(data.min_single_ank),
          max_single_ank: getValue(data.max_single_ank),
          min_jodi_ank: getValue(data.min_jodi_ank),
          max_jodi_ank: getValue(data.max_jodi_ank),
          min_pana_ank: getValue(data.min_pana_ank),
          max_pana_ank: getValue(data.max_pana_ank),

          openTime: data.withdraw_timings?.split(" - ")[0] || "",
          closeTime: data.withdraw_timings?.split(" - ")[1] || "",
          whatsapp_deposit_option: data.whatsapp_deposit_option || "active",
          withdraw_option: data.withdraw_option || "active",
          global_betting: data.global_betting || false,
          closeWeek: data.closeWeek || "Friday",
        });

        setFetchingData(false);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching settings:", error);
        alert("Failed to fetch settings");
        setLoading(false);
        setFetchingData(false);
      }
    };

    fetchSettings();
  }, []);

  // Handle input change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Generate time options
  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 1; hour <= 12; hour++) {
      for (let minute of ["00", "30"]) {
        times.push(`${hour}:${minute} AM`);
        times.push(`${hour}:${minute} PM`);
      }
    }
    return times;
  };

  // Handle form submission (Update API)
  const handleSubmit = async () => {
    if (!formData.id) {
      alert("Error: Missing settings ID!");
      return;
    }

    try {
      setLoading(true);
      const updatedData = {
        ...formData,
        withdraw_timings: `${formData.openTime} - ${formData.closeTime}`,
      };

      await axiosInstance.put(`/api/settings/general/${formData.id}`, updatedData);
      alert("Settings updated successfully!");
      setLoading(false);
    } catch (error) {
      console.error("Error updating settings:", error);
      alert("Failed to update settings");
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      {/* Fullscreen loading overlay */}
      {(loading || fetchingData) && (
        <div className="absolute top-0 left-0 w-full h-full bg-gray-500 opacity-50 flex items-center justify-center z-10">
          <div className="text-white text-xl">Loading...</div>
        </div>
      )}

      <h2 className="text-xl font-bold mb-4">Settings Update</h2>

      {fetchingData && (
        <div className="flex justify-center items-center py-4">
          <div className="spinner"></div> {/* Loading spinner */}
          <p className="ml-2">Loading settings...</p>
        </div>
      )}

      {/* 3-column grid for general inputs */}
      <div className="grid grid-cols-3 gap-4">
        {Object.keys(formData).map(
          (key) =>
            key !== "id" &&
            key !== "global_betting" &&
            key !== "openTime" &&
            key !== "closeTime" &&
            key !== "openWeek" &&
            key !== "closeWeek" &&
            key !== "withdraw_option" &&
            key !== "whatsapp_deposit_option" && (
              <div key={key} className="flex flex-col">
                <label className="text-sm font-semibold capitalize">
                  {key.replace(/_/g, " ")}
                </label>
                <input
                  type={
                    key.includes("min_") || key.includes("max_") || key.includes("rate") || key.includes("amount")
                      ? "number"
                      : "text"
                  }
                  name={key}
                  value={formData[key] ?? ""}
                  onChange={handleChange}
                  className="border border-gray-300 p-2 rounded-md mt-1"
                />

              </div>
            )
        )}

      </div>

      {/* New row for Time and WhatsApp Deposit Option */}
      <div className="grid grid-cols-3 gap-4 mt-4">
        {/* Open Time Selection */}
        <div className="flex flex-col">
          <label className="text-sm font-semibold">Withdraw Open Time</label>
          <select
            name="openTime"
            value={formData.openTime}
            onChange={handleChange}
            className="border border-gray-300 p-2 rounded-md mt-1"
            disabled={loading}
          >
            {generateTimeOptions().map((time) => (
              <option key={time} value={time}>
                {time}
              </option>
            ))}
          </select>
        </div>

        {/* Close Time Selection */}
        <div className="flex flex-col">
          <label className="text-sm font-semibold">Withdraw Close Time</label>
          <select
            name="closeTime"
            value={formData.closeTime}
            onChange={handleChange}
            className="border border-gray-300 p-2 rounded-md mt-1"
            disabled={loading}
          >
            {generateTimeOptions().map((time) => (
              <option key={time} value={time}>
                {time}
              </option>
            ))}
          </select>
        </div>

        {/* WhatsApp Deposit Option */}
        <div className="flex flex-col">
          <label className="text-sm font-semibold">
            WhatsApp Deposit Option
          </label>
          <select
            name="whatsapp_deposit_option"
            value={formData.whatsapp_deposit_option}
            onChange={handleChange}
            className="border border-gray-300 p-2 rounded-md mt-1"
            disabled={loading}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4 mt-4">

        {/* Close Week Selection */}
        <div className="flex flex-col">
          <label className="text-sm font-semibold">Withdraw Close Week</label>
          <select
            name="closeWeek"
            value={formData.closeWeek}
            onChange={handleChange}
            className="border border-gray-300 p-2 rounded-md mt-1"
            disabled={loading}
          >
            <option value="Monday">Monday</option>
            <option value="Tuesday">Tuesday</option>
            <option value="Wednesday">Wednesday</option>
            <option value="Thursday">Thursday</option>
            <option value="Friday">Friday</option>
            <option value="Saturday">Saturday</option>
            <option value="Sunday">Sunday</option>
          </select>
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-semibold">
            Withdraw Option
          </label>
          <select
            name="withdraw_option"
            value={formData.withdraw_option}
            onChange={handleChange}
            className="border border-gray-300 p-2 rounded-md mt-1"
            disabled={loading}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Global Betting Checkbox */}
      <div className="flex items-center mt-4">
        <input
          type="checkbox"
          name="global_betting"
          checked={formData.global_betting}
          onChange={handleChange}
          className="w-5 h-5 mr-2"
          disabled={loading}
        />
        <label className="text-sm font-semibold">Enable Global Betting</label>
      </div>

      {/* Centered Update Button */}
      <div className="grid grid-cols-3 mt-4">
        <div></div>
        <div className="flex justify-center">
          <button
            onClick={handleSubmit}
            className="bg-[#556EE6] text-white px-4 py-2 rounded-md"
            disabled={loading}
          >
            {loading ? (
              <div className="flex justify-center items-center">
                <div className="spinner"></div>
                Saving...
              </div>
            ) : (
              "Update"
            )}
          </button>
        </div>
        <div></div>
      </div>
    </div>
  );
};

export default SettingsForm;
